import { ColDef, ColumnState } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FC, useMemo } from 'react';
import { LocalStorageKey } from 'src/constants';

import { getLocalStorageValue, setLocalStorageValue } from 'src/utils/common';
import { ProcessedOutboundTransaction } from 'src/utils/transaction-util';

import PrintWindow from 'src/components/PrintWindow/PrintWindow';

interface DailyOutboundTransactionTableProps {
  rowData: ProcessedOutboundTransaction[];
}

const defaultColDef: ColDef<ProcessedOutboundTransaction> = {
  sortable: true,
  resizable: true,
  filter: true,
};

const DailyOutboundTransactionTableForPrint: FC<DailyOutboundTransactionTableProps> = ({ rowData }) => {
  const columnDefs: ColDef<ProcessedOutboundTransaction>[] = useMemo(
    () => [
      {
        headerName: '구분',
        field: 'productSupplier.groupId',
        width: 76,
      },
      {
        headerName: '재고명',
        field: 'inventory.inventoryName',
        width: 210 + 40,
        wrapText: true,
        autoHeight: true,
        cellStyle: (params) => ({
          lineHeight: '1.1em',
          display: 'flex',
          alignItems: 'center',
          letterSpacing: '-0.025em',
          wordBreak: 'keep-all',
          color: params.data?.isPreOrder ? '#ff1493' : 'inherit',
        }),
      },
      {
        headerName: '필요',
        field: 'totalQuantity',
        width: 76,
        cellStyle: { backgroundColor: '#ffff00' },
      },
      {
        headerName: '발주',
        field: 'purchaseQuantity',
        width: 76,
        cellStyle: (params) => {
          return params.value > 0 ? { backgroundColor: '#b6e1cd' } : null;
        },
      },
      {
        headerName: '재고',
        field: 'inventoryQuantity',
        headerTooltip: '개별 (묶음/묶음을 제외한 개별)',
        width: 96,
        valueFormatter: (params) => {
          const inventory = params.data?.inventory;
          const value = params.value;
          if (inventory?.uom.bundle) {
            const ratio = inventory.uom.bundle.ratio;
            const bundle = Math.floor(value / ratio);
            if (bundle > 0) {
              const ea = value % ratio;
              return `${value} (${bundle}${ea > 0 ? `/${ea}` : ''})`;
            }
            return value;
          }
          return value;
        },
        cellStyle: (params) => {
          return params.value > 0 ? { backgroundColor: '#d0e2f4' } : null;
        },
      },
      {
        headerName: '위치',
        field: 'inventoryBinLocation',
        width: 84,
      },
      {
        headerName: '거래처',
        field: 'productSupplier.name',
        width: 130,
      },
    ],
    []
  );
  return (
    <PrintWindow>
      <div className='ag-theme-alpine bgWhite'>
        <AgGridReact
          onGridReady={(e) => {
            const filterModel = getLocalStorageValue<any>(LocalStorageKey.dailyOutboundTxnTableFilterModel);
            const columnState = getLocalStorageValue<ColumnState[]>(LocalStorageKey.dailyOutboundTxnTableColumnState);
            if (filterModel) {
              e.api.setFilterModel(filterModel);
            }
            if (columnState) {
              e.columnApi.applyColumnState({ state: columnState, applyOrder: true });
            }
          }}
          onFilterChanged={(e) => {
            const filterModel = e.api.getFilterModel();
            setLocalStorageValue(LocalStorageKey.dailyOutboundTxnTableFilterModel, filterModel);
          }}
          onSortChanged={(e) => {
            const columnState = e.columnApi.getColumnState();
            setLocalStorageValue(LocalStorageKey.dailyOutboundTxnTableColumnState, columnState);
          }}
          rowData={rowData}
          getRowId={(params) => params.data.inventoryId}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          domLayout='print'
          rowHeight={48}
          tooltipShowDelay={300}
          alwaysShowHorizontalScroll={true}
        />
      </div>
    </PrintWindow>
  );
};

export default DailyOutboundTransactionTableForPrint;
